import { useEffect, useState } from 'react';
import {
  getTelegramConfig,
  getTelegramToken,
  sendRoomToTelegram,
  type TelegramConfig,
} from '../lib/api.js';
import { Button } from '@/components/ui/button';

interface TelegramInviteProps {
  code: string;
  name: string;
}

type State = 'idle' | 'sending' | 'sent' | 'no_chat' | 'failed';

/**
 * «Прислать ссылку в телеграм».
 *
 * Ссылку всё равно куда-то пересылать, и чаще всего — в телеграм. Бот
 * присылает её самому себе, откуда её удобно переслать друзьям, не
 * перекладывая руками через буфер обмена.
 *
 * Кнопки нет, пока человек не вошёл: без входа боту некому писать, и
 * показывать заведомо нерабочую кнопку незачем.
 */
export function TelegramInvite({ code, name }: TelegramInviteProps): JSX.Element | null {
  const [config, setConfig] = useState<TelegramConfig | null>(null);
  const [state, setState] = useState<State>('idle');

  useEffect(() => {
    void getTelegramConfig()
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, botUsername: null, required: false }));
  }, []);

  if (!config?.enabled || !getTelegramToken()) return null;

  const send = async (): Promise<void> => {
    setState('sending');
    try {
      const result = await sendRoomToTelegram(code, name);
      setState(result.sent ? 'sent' : result.reason === 'no_chat' ? 'no_chat' : 'failed');
    } catch {
      setState('failed');
    }
  };

  return (
    <div className="tg-invite">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={state === 'sending' || state === 'sent'}
        onClick={() => void send()}
      >
        {state === 'sent' ? 'Отправлено в телеграм' : 'Прислать в телеграм'}
      </Button>

      {state === 'no_chat' && config.botUsername && (
        <p className="invite__note">
          Сначала откройте{' '}
          <a
            href={`https://t.me/${config.botUsername}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            @{config.botUsername}
          </a>{' '}
          и нажмите «Старт»: первым написать бот не может.
        </p>
      )}

      {state === 'failed' && (
        <p className="invite__note">Телеграм не принял сообщение. Скопируйте ссылку руками.</p>
      )}
    </div>
  );
}
